// Tax Policy layer (phase 4) — the MANUAL tax-group writer's dialog.
// Pins an estimate/job/invoice to an operator-chosen tax group (with a
// required reason), or clears the pin so the document re-resolves from its
// address and rules. The server reprices the document either way.
import { useState } from 'react';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Label } from '@/components/atoms/label';
import { Input } from '@/components/atoms/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { useTaxGroups } from '@/hooks/taxes/useTaxGroups';
import {
  useSetDocumentTaxGroup,
  type TaxGroupOverrideDocument,
} from '@/hooks/taxes/useTaxPolicies';
import { formatTaxRate } from '@/lib/tax/tax-utils';

interface TaxGroupOverrideDialogProps {
  open: boolean;
  onClose: () => void;
  document: TaxGroupOverrideDocument;
  documentId: string;
  /** The document's current group id — pre-selected when pinned. */
  currentTaxGroupId?: string | null;
  /** True when the document is currently pinned (taxResolvedFrom MANUAL). */
  currentlyManual?: boolean;
  /** Invalidate/refetch the document after the server repriced it. */
  onSaved?: () => void;
}

export function TaxGroupOverrideDialog({
  open,
  onClose,
  document,
  documentId,
  currentTaxGroupId,
  currentlyManual,
  onSaved,
}: TaxGroupOverrideDialogProps) {
  const { data: taxGroups } = useTaxGroups();
  const [taxGroupId, setTaxGroupId] = useState<string>(currentTaxGroupId ?? '');
  const [reason, setReason] = useState('');
  // Re-seed on every open so a cancelled draft (or a since-cleared pin)
  // never leaves a stale selection armed for the next session.
  const [seededOpen, setSeededOpen] = useState(false);
  if (open !== seededOpen) {
    setSeededOpen(open);
    if (open) {
      setTaxGroupId(currentTaxGroupId ?? '');
      setReason('');
    }
  }
  const mutation = useSetDocumentTaxGroup({
    onSuccess: () => {
      onSaved?.();
      onClose();
    },
  });

  // Placeholder groups (POPULATING / NO_ZIPTAX_DATA) are unusable on the
  // backend — offering them here would 400 on pin.
  const groups = (taxGroups ?? []).filter((g) => g.isActive !== false && !g.placeholderReason);
  const canPin = taxGroupId.length > 0 && reason.trim().length >= 3;

  const pin = () => mutation.mutate({ document, documentId, taxGroupId, reason: reason.trim() });
  const clear = () => mutation.mutate({ document, documentId, taxGroupId: null });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change tax group</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          {currentlyManual ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline" className="text-[11px]">
                Pinned
              </Badge>
              This document is pinned to a tax group; it will not follow the address until the pin
              is cleared.
            </div>
          ) : null}
          <div className="grid gap-1.5">
            <Label>Tax group</Label>
            <Select value={taxGroupId} onValueChange={setTaxGroupId}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a tax group" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name} ({formatTaxRate(g.combinedRate)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="pin-reason">Reason</Label>
            <Input
              id="pin-reason"
              placeholder="Why this group applies here (min 3 characters)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Pinning reprices the document under the chosen group and keeps it there across edits.
            Tax rules still limit what is taxable.
          </p>
        </div>
        <DialogFooter className="gap-2">
          {currentlyManual ? (
            <Button
              variant="outline"
              onClick={clear}
              disabled={mutation.isPending}
              className="mr-auto"
            >
              Clear pin
            </Button>
          ) : null}
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={pin} disabled={!canPin || mutation.isPending}>
            Pin tax group
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
